import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { BookOpen, IndianRupee, Layers, School } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { formatCurrency } from '@/lib/format';
import type { StudentFeeAccount } from '@/types/fees';
import { feesApi } from '../api/fees.api';
import {
  EMPTY_FEE_CHARGE_FORM,
  feeChargeFormResolver,
  feeChargeSchema,
  type FeeChargeFormValues,
} from '../schemas/fee.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

export interface SetFeeChargeModalProps {
  account: StudentFeeAccount | null;
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSaved: (account: StudentFeeAccount, wasOpened: boolean) => void;
}

/**
 * What this student is charged for the session, and what has been waived.
 *
 * One dialog for opening an account and for correcting one: the fields are
 * identical, and a second component differing only in which verb it used would
 * be the copy-paste §3 exists to prevent.
 *
 * A concession asks for a reason, and the reason is not decoration — a discount
 * on a child's fees is a decision somebody made about one family, and it has to
 * be accountable years later.
 *
 * What has been *collected* is not on this form and cannot be: it is the sum of
 * the receipts, and the honest way to change it is to cancel one.
 */
export function SetFeeChargeModal({
  account,
  isOpen,
  onClose,
  onBack,
  onSaved,
}: SetFeeChargeModalProps) {
  const [formError, setFormError] = useState<string | null>(null);
  const isOpening = account !== null && !account.hasAccount;

  const form = useForm<FeeChargeFormValues>({
    resolver: feeChargeFormResolver,
    defaultValues: EMPTY_FEE_CHARGE_FORM,
  });
  useLiveFormErrors(form);
  const {
    register,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (!isOpen || !account) return;
    setFormError(null);
    form.reset(
      account.hasAccount
        ? {
            tuitionFee: account.tuition.fee,
            tuitionDiscount: account.tuition.discount,
            booksFee: account.books.fee,
            booksDiscount: account.books.discount,
            otherFee: account.other.fee,
            otherDiscount: account.other.discount,
            concessionReason: account.concessionReason ?? '',
            dueDate: account.dueDate ?? '',
          }
        : EMPTY_FEE_CHARGE_FORM,
    );
  }, [isOpen, account, form]);

  const payable =
    Math.max(0, Number(watch('tuitionFee') || 0) - Number(watch('tuitionDiscount') || 0)) +
    Math.max(0, Number(watch('booksFee') || 0) - Number(watch('booksDiscount') || 0)) +
    Math.max(0, Number(watch('otherFee') || 0) - Number(watch('otherDiscount') || 0));

  const onSubmit = form.handleSubmit(async (values) => {
    if (!account) return;
    setFormError(null);

    try {
      const payload = feeChargeSchema.parse(values);
      const saved = await feesApi.setCharge(account.student.uuid, payload);
      onSaved(saved, isOpening);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(form.setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  /** The money fields, so a fee block cannot be pointed at the reason box. */
  const money = (field: keyof Omit<FeeChargeFormValues, 'concessionReason' | 'dueDate'>) => ({
    type: 'number' as const,
    inputMode: 'numeric' as const,
    min: 0,
    className: 'font-mono',
    leftIcon: <IndianRupee className="h-4 w-4" />,
    error: errors[field]?.message,
    ...register(field),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onBack={onBack}
      size="lg"
      title={
        account
          ? isOpening
            ? `Set fees for ${account.student.fullName}`
            : `Edit fees for ${account.student.fullName}`
          : 'Set fees'
      }
      description={
        account
          ? `For the ${account.academicYear.name} session. Receipts already issued are not affected.`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} isLoading={isSubmitting}>
            {isSubmitting ? 'Saving…' : isOpening ? 'Open the account' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save these fees">
            {formError}
          </Alert>
        )}

        {account && Number(account.collected) > 0 && (
          <Alert tone="info" title={`${formatCurrency(account.collected)} already collected`}>
            A fee cannot be set below what has been taken against it. If a receipt was wrong,
            cancel it first.
          </Alert>
        )}

        <div className="rounded-card border border-line bg-surface-2 p-4">
          <h3 className="lbl flex items-center gap-1.5 text-accent">
            <School className="h-3.5 w-3.5" aria-hidden />
            Tuition
          </h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Input label="Fee for the year" {...money('tuitionFee')} />
            <Input label="Concession" {...money('tuitionDiscount')} />
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface-2 p-4">
          <h3 className="lbl flex items-center gap-1.5 text-accent">
            <BookOpen className="h-3.5 w-3.5" aria-hidden />
            Books and study materials
          </h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Input label="Fee for the year" {...money('booksFee')} />
            <Input label="Concession" {...money('booksDiscount')} />
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface-2 p-4">
          <h3 className="lbl flex items-center gap-1.5 text-accent">
            <Layers className="h-3.5 w-3.5" aria-hidden />
            Other fees
          </h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Input label="Fee for the year" {...money('otherFee')} />
            <Input label="Concession" {...money('otherDiscount')} />
          </div>
        </div>

        <Input
          label="Reason for the concession"
          placeholder="Sibling concession, staff ward, RTE"
          autoComplete="off"
          hint="Required whenever anything is waived — a discount must always be accountable."
          error={errors.concessionReason?.message}
          {...register('concessionReason')}
        />

        {/* Optional throughout: an institute that does not work to due dates
            leaves it empty, and emptying it again takes the deadline off. */}
        <Input
          label="Fees due by (optional)"
          type="date"
          autoComplete="off"
          hint="Sets when this family's fees fall due, so the counter can list them under “due soon”. Leave blank if your institute does not work to due dates."
          error={errors.dueDate?.message}
          {...register('dueDate')}
        />

        <div className="rounded-card border border-line bg-accent-soft p-4 text-sm">
          <span className="text-ink-2">
            This family will owe{' '}
            <strong className="font-mono font-[560] text-ink">{formatCurrency(payable)}</strong>{' '}
            for the year.
          </span>
        </div>
      </form>
    </Modal>
  );
}
